import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Customer, VerifyStrategyType } from '@ca/common/types'
import Button from '@ui/buttons/Button'
import CustomerHeader from '@user/customer/TileUser/CustomerHeader'
import { IconName } from '@ui/elements/Icon'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import SectionRow from '@ui/elements/SectionRow'
import type { StatusProps } from '@ui/elements/Status/types'
import { LoadingFooter } from '@ui/lists/LoadingFooter'

interface Props {
  customer: Customer | null
  onClose: () => void
  onSelectStrategy: (customer: Customer, strategy: VerifyStrategyType) => void
  hideStaffStrategy?: boolean
  headerBadges?: StatusProps[]
  isVerifying?: boolean
}

export default function CustomerAuthModal({
  customer,
  onClose,
  onSelectStrategy,
  hideStaffStrategy,
  headerBadges,
  isVerifying,
}: Props) {
  const { t } = useTranslation(['auth', 'modals'])
  const tHeader = 'auth:modal.header'
  const tMethods = 'auth:modal.methods'
  const isOpen = !!customer

  const selectStrategy = (strategy: VerifyStrategyType) => {
    if (!customer || isVerifying) return
    onSelectStrategy(customer, strategy)
  }

  const methods: Array<{
    strategy: VerifyStrategyType
    icon: IconName
    info?: string
    cta: string
    disabled: boolean
  }> = [
    {
      strategy: 'phone',
      icon: 'message-circle-2',
      info: customer?.mobile ?? undefined,
      cta: t('ui:send'),
      disabled: !customer?.mobile,
    },
    {
      strategy: 'email',
      icon: 'mail',
      info: customer?.email ?? undefined,
      cta: t('ui:send'),
      disabled: !customer?.email,
    },
    ...(hideStaffStrategy
      ? []
      : [
          {
            strategy: 'staff' as const,
            icon: 'id' as const,
            cta: t('ui:select'),
            disabled: false,
          },
        ]),
  ]

  return (
    <ModalWide
      center
      open={isOpen}
      close={onClose}
      title={t(`${tHeader}.title`)}
      desc={t(`${tHeader}.desc`)}
      hideBottomSpace
      cardComponentProps={{
        justifyContent: 'flex-end',
      }}
      showClose={!isVerifying}
    >
      {customer && (
        <View className="relative">
          <View className="px-8">
            <View className="rounded-xl border border-black/10">
              <CustomerHeader
                standalone
                data={customer}
                badges={headerBadges}
              />
            </View>
          </View>

          <View className="gap-8 p-8">
            {methods.map(({ strategy, ...method }) => (
              <SectionRow
                key={strategy}
                title={t(`${tMethods}.${strategy}.title`)}
                desc={t(`${tMethods}.${strategy}.desc`)}
                icon={method.icon}
                info={method.info}
                disabled={method.disabled}
                variant={method.disabled ? 'disabled' : 'default'}
                onPress={() => selectStrategy(strategy)}
                actionComponent={
                  <Button
                    noninteractive
                    variant="outlined"
                    size="xs"
                    label={method.cta}
                    disabled={method.disabled}
                  />
                }
              />
            ))}
          </View>
        </View>
      )}
      {isVerifying && (
        <View className="absolute inset-0 items-center justify-center rounded-xl bg-white/80">
          <LoadingFooter className="p-0" />
        </View>
      )}
    </ModalWide>
  )
}
